// ============================================================================
// CONFIG SETTINGS PANEL
// ============================================================================
// Right panel: scope selector, settings list, and save/discard footer

import React, { useMemo, useCallback, useState } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { propertyService, ratePlanService } from 'api/services';
import ConfigSettingField from './ConfigSettingField';
import ApprovalRoutingSection from './ApprovalRoutingSection';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

interface ConfigSettingsPanelProps {
  activeGroup: ConfigGroup | null;
  scopeType: ScopeType;
  onScopeTypeChange: (scope: ScopeType) => void;
  selectedPropertyId: string | null;
  onPropertySelect: (propertyId: string | null) => void;
  selectedRatePlanId: string | null;
  onRatePlanSelect: (ratePlanId: string | null) => void;
  effectiveValues: ConfigEffectiveValue[];
  definitions: ConfigDefinition[];
  editedValues: Record<string, ConfigValueUpdate>;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  hasChanges: boolean;
  saving: boolean;
  loading: boolean;
  /**
   * Approval-routing edits live in their own table (approval.tbl_approval_hierarchy)
   * with their own endpoints, so they are NOT folded into `editedValues` /
   * `saveChanges` — two very different payloads. The section owns its edits and
   * its own Save, and reports dirtiness up so the screen's group/scope-change
   * guard covers it. See ApprovalRoutingSection.
   */
  onRoutingDirtyChange: (dirty: boolean) => void;
  routingDiscardSignal: number;
}

const ConfigSettingsPanel: React.FC<ConfigSettingsPanelProps> = ({
  activeGroup,
  scopeType,
  onScopeTypeChange,
  selectedPropertyId,
  onPropertySelect,
  selectedRatePlanId,
  onRatePlanSelect,
  effectiveValues,
  definitions,
  editedValues,
  onValueChange,
  onResetValue,
  onSave,
  onDiscard,
  hasChanges,
  saving,
  loading,
  onRoutingDirtyChange,
  routingDiscardSignal,
}) => {
  // Load property options for async SyutSelect
  const loadPropertyOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const res = await propertyService.getAll({
        searchTerm: inputValue || undefined,
        pageSize: inputValue ? 50 : 500,
        includeInactive: false,
      });
      return res.data.map((p) => ({
        value: p.id,
        label: `${p.propertyCode} - ${p.propertyName}`,
      }));
    },
    []
  );

  // Load rate plan options filtered by selected property
  const loadRatePlanOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      if (!selectedPropertyId) return [];
      const res = await ratePlanService.getAll({
        propertyId: selectedPropertyId,
        searchTerm: inputValue || undefined,
        pageSize: inputValue ? 50 : 500,
        includeInactive: false,
      });
      return res.data.map((rp) => ({
        value: rp.id,
        label: rp.rateName,
      }));
    },
    [selectedPropertyId]
  );

  // Check if any definition in the current group supports rate plan scope
  const hasRatePlanScopeSettings = useMemo(() => {
    return definitions.some((d) => d.maxScopeLevel >= 2);
  }, [definitions]);

  // Sort definitions by displayOrder and match with effective values
  const sortedSettings = useMemo(() => {
    return definitions
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((def) => {
        const ev = effectiveValues.find((v) => v.configKey === def.configKey);
        return { definition: def, effectiveValue: ev };
      })
      .filter((item) => item.effectiveValue != null);
  }, [definitions, effectiveValues]);

  // Cross-field gating: when `block_extension_allowed` is OFF, the entire
  // extension trio (max_block_hold_hours, max_extensions, extension_hours)
  // is rendered disabled. Values are preserved so toggling back ON restores
  // the previous numbers without retyping; saving while disabled is the
  // panel's responsibility — see comment on `getGatedDisabled` below.
  const liveValue = useCallback(
    (key: string): string | undefined => {
      const edited = editedValues[key]?.value;
      if (edited !== undefined) return edited;
      const ev = effectiveValues.find((v) => v.configKey === key);
      return ev?.effectiveValue ?? definitions.find((d) => d.configKey === key)?.defaultValue ?? undefined;
    },
    [editedValues, effectiveValues, definitions]
  );

  const blockExtensionAllowed = useMemo(() => {
    const raw = liveValue('block_extension_allowed');
    return raw ? raw.toLowerCase() === 'true' : true;
  }, [liveValue]);

  /**
   * Which half of the Approval Rules group is on screen. Two genuinely different
   * things live in this group — the RULES (config values: is approval required,
   * at what threshold) and the ROUTING (which role approves, a different table
   * entirely). Stacked on one page they read as one long list and the reader
   * cannot tell where one ends and the other begins.
   *
   * Both stay MOUNTED and are hidden with display:none rather than conditionally
   * rendered: the routing section holds unsaved role picks in its own state, and
   * unmounting it to switch tabs would silently discard them.
   */
  const [approvalTab, setApprovalTab] = useState<'rules' | 'routing'>('rules');

  const discountApprovalRequired = useMemo(() => {
    const raw = liveValue('discount_approval_required');
    return raw ? raw.toLowerCase() === 'true' : true;
  }, [liveValue]);

  const getGatedDisabled = useCallback(
    (configKey: string): { disabled: boolean; reason?: string } => {
      if (
        !blockExtensionAllowed &&
        (configKey === 'max_block_hold_hours' ||
          configKey === 'max_extensions' ||
          configKey === 'extension_hours')
      ) {
        return {
          disabled: true,
          reason: 'Ignored while "Block Extension Allowed" is off — only Block Duration applies.',
        };
      }
      // The threshold's own description already says it applies only while
      // Discount Approval Required is on; it just was not enforced, so the
      // field invited an edit that changed nothing.
      if (!discountApprovalRequired && configKey === 'high_discount_threshold') {
        return {
          disabled: true,
          reason: 'Ignored while "Discount Approval Required" is off — no discount routes for approval.',
        };
      }
      return { disabled: false };
    },
    [blockExtensionAllowed, discountApprovalRequired]
  );

  // Plain-English summary of the effective lifecycle, shown above the field
  // list whenever the active group is BLOCKING_POLICY. Reads the same live
  // values so it reflects edited-but-unsaved overrides immediately.
  const blockingSummary = useMemo(() => {
    if (activeGroup?.groupKey !== 'BLOCKING_POLICY') return null;
    const duration = parseInt(liveValue('block_duration_hours') ?? '24', 10);
    if (!blockExtensionAllowed) {
      return `Initial block: ${duration}h. Extensions disabled — total hold cannot exceed ${duration}h.`;
    }
    const maxHold  = parseInt(liveValue('max_block_hold_hours') ?? '24', 10);
    const maxExt   = parseInt(liveValue('max_extensions') ?? '0', 10);
    const extHours = parseInt(liveValue('extension_hours') ?? '0', 10);
    const projected = duration + maxExt * extHours;
    const effectiveMax = Math.min(maxHold, projected);
    return `Initial block: ${duration}h. Extensions allowed: up to ${maxExt} × ${extHours}h, capped at now + ${maxHold}h. Maximum total hold: ${effectiveMax}h (= min of ${maxHold}h cap and ${duration} + ${maxExt}×${extHours} = ${projected}h).`;
  }, [activeGroup, blockExtensionAllowed, liveValue]);

  if (!activeGroup) {
    return (
      <div className="config-settings-panel">
        <div className="config-empty">Select a group to view settings</div>
      </div>
    );
  }

  return (
    <div className="config-settings-panel">
      {/* Header */}
      <div className="config-panel-header">
        <h2>{activeGroup.displayName}</h2>
        {activeGroup.description && <p>{activeGroup.description}</p>}
      </div>

      {/* Scope bar */}
      <div className="config-scope-bar">
        <div className="config-scope-toggle">
          <button
            type="button"
            className={`config-scope-toggle-btn${
              scopeType === 'SYSTEM' ? ' active' : ''
            }`}
            onClick={() => onScopeTypeChange('SYSTEM')}
          >
            System Defaults
          </button>
          <button
            type="button"
            className={`config-scope-toggle-btn${
              scopeType === 'PROPERTY' ? ' active' : ''
            }`}
            onClick={() => onScopeTypeChange('PROPERTY')}
          >
            Property Override
          </button>
          {hasRatePlanScopeSettings && (
            <button
              type="button"
              className={`config-scope-toggle-btn${
                scopeType === 'RATE_PLAN' ? ' active' : ''
              }`}
              onClick={() => onScopeTypeChange('RATE_PLAN')}
            >
              Rate Plan Override
            </button>
          )}
        </div>
        {(scopeType === 'PROPERTY' || scopeType === 'RATE_PLAN') && (
          <div className="config-scope-property-select">
            <SyutSelect
              isAsync
              loadOptions={loadPropertyOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={selectedPropertyId ?? undefined}
              onChange={(value: string | string[] | null) =>
                onPropertySelect(typeof value === 'string' ? value : null)
              }
              placeholder="Select a property..."
              isClearable
              size="sm"
            />
          </div>
        )}
        {scopeType === 'RATE_PLAN' && selectedPropertyId && (
          <div className="config-scope-property-select">
            <SyutSelect
              key={selectedPropertyId}
              isAsync
              loadOptions={loadRatePlanOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={selectedRatePlanId ?? undefined}
              onChange={(value: string | string[] | null) =>
                onRatePlanSelect(typeof value === 'string' ? value : null)
              }
              placeholder="Select a rate plan..."
              isClearable
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Rules / Routing tabs — only this group has two halves. */}
      {activeGroup.groupKey === 'APPROVAL_RULES' && (
        <div className="config-subtab-bar">
          <button
            type="button"
            className={`config-subtab${approvalTab === 'rules' ? ' active' : ''}`}
            onClick={() => setApprovalTab('rules')}
          >
            Approval Rules
          </button>
          <button
            type="button"
            className={`config-subtab${approvalTab === 'routing' ? ' active' : ''}`}
            onClick={() => setApprovalTab('routing')}
          >
            Approval Routing
          </button>
          <span className="config-subtab-hint">
            {approvalTab === 'rules'
              ? 'Whether each type needs approval, and at what limit'
              : 'Who approves each type — a type is inactive until its rule is switched on'}
          </span>
        </div>
      )}

      {/* Group-level summary (currently only BLOCKING_POLICY) */}
      {blockingSummary && (
        <div
          style={{
            margin: '0 1.25rem',
            padding: '0.75rem 1rem',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            fontSize: '0.8125rem',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: 'var(--text-primary)' }}>Effective lifecycle: </strong>
          {blockingSummary}
        </div>
      )}

      {/* Settings list */}
      <div
        className="config-settings-list"
        style={activeGroup.groupKey === 'APPROVAL_RULES' && approvalTab !== 'rules'
          ? { display: 'none' } : undefined}
      >
        {loading ? (
          <div className="config-loading">Loading settings...</div>
        ) : (scopeType === 'PROPERTY' || scopeType === 'RATE_PLAN') && !selectedPropertyId ? (
          <div className="config-empty">
            Select a property to view its configuration overrides
          </div>
        ) : scopeType === 'RATE_PLAN' && selectedPropertyId && !selectedRatePlanId ? (
          <div className="config-empty">
            Select a rate plan to view its configuration overrides
          </div>
        ) : sortedSettings.length === 0 ? (
          <div className="config-empty">No settings found for this group</div>
        ) : (
          sortedSettings.map(({ definition, effectiveValue }) => {
            const gate = getGatedDisabled(definition.configKey);
            return (
              <ConfigSettingField
                key={definition.configKey}
                definition={definition}
                effectiveValue={effectiveValue!}
                editedValue={editedValues[definition.configKey]}
                onValueChange={onValueChange}
                onResetValue={onResetValue}
                scopeType={scopeType}
                disabled={gate.disabled}
                disabledReason={gate.reason}
              />
            );
          })
        )}
      </div>

      {/* Approval routing — the AUTHORITY half of this group, on its own tab.
          Kept mounted while the Rules tab is showing so unsaved role picks
          survive a tab switch; `liveValue` means a rule toggled on the other tab
          flips a routing row active/inactive immediately, before either half is
          saved. */}
      {activeGroup.groupKey === 'APPROVAL_RULES' && (
        <div
          className="config-routing-pane"
          style={approvalTab !== 'routing' ? { display: 'none' } : undefined}
        >
          <ApprovalRoutingSection
            scopeType={scopeType}
            selectedPropertyId={selectedPropertyId}
            onDirtyChange={onRoutingDirtyChange}
            discardSignal={routingDiscardSignal}
            liveValue={liveValue}
          />
        </div>
      )}

      {/* Footer bar */}
      {(hasChanges || saving) && (
        <div className="config-footer-bar">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDiscard}
            disabled={saving}
          >
            Discard Changes
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfigSettingsPanel;
